import { useState, type ReactNode } from "react";
import { AlertCircle, Check, Copy, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export const EMPTY_VALUE = "-";

const isEmpty = (value: ReactNode) =>
  value === null ||
  value === undefined ||
  value === "" ||
  value === EMPTY_VALUE;

/**
 * A titled card grouping a set of DetailRows. Used by record detail views
 * (transactions, promo codes, …) so their read views stay visually consistent.
 */
export const DetailSection = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <Card className="border border-border/60 shadow-sm">
    <CardHeader className="flex flex-row items-center justify-between gap-3 border-b border-border/60 px-6 py-4 space-y-0">
      <CardTitle className="z-h3 text-foreground">{title}</CardTitle>
      {action}
    </CardHeader>
    <CardContent className="divide-y divide-border/60 px-6 py-0">
      {children}
    </CardContent>
  </Card>
);

/**
 * A label/value row inside a DetailSection. Primitive values render with
 * optional mono styling and an optional copy affordance; node values render
 * as-is (badges, stacked text, …).
 */
export const DetailRow = ({
  label,
  value,
  mono = false,
  copyable = false,
  valueClassName,
}: {
  label: string;
  value?: ReactNode;
  mono?: boolean;
  copyable?: boolean;
  valueClassName?: string;
}) => {
  const isPrimitive =
    typeof value === "string" ||
    typeof value === "number" ||
    value === null ||
    value === undefined;
  const empty = isPrimitive && isEmpty(value as ReactNode);
  return (
    <div className="flex min-h-12 items-start gap-4 py-3">
      <span className="z-label w-[160px] shrink-0 pt-0.5 text-muted-foreground">
        {label}
      </span>
      <div className="flex min-w-0 flex-1 items-start gap-2">
        {isPrimitive ? (
          <span
            className={cn(
              "z-body min-w-0 break-all",
              mono && !empty && "font-mono",
              empty ? "text-muted-foreground/60" : "text-foreground",
              valueClassName
            )}
          >
            {empty ? "—" : (value as string | number)}
          </span>
        ) : (
          <div className={cn("min-w-0 flex-1", valueClassName)}>{value}</div>
        )}
        {copyable && isPrimitive && !empty ? (
          <CopyButton value={String(value)} />
        ) : null}
      </div>
    </div>
  );
};

export const CopyButton = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable; no-op
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={handleCopy}
      aria-label={copied ? "Copied" : "Copy to clipboard"}
      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-primary" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  );
};

export const LoadingState = ({
  label,
  compact = false,
}: {
  label: string;
  compact?: boolean;
}) => (
  <div
    className={cn(
      "flex items-center justify-center text-muted-foreground",
      compact ? "py-10" : "py-16"
    )}
  >
    <Loader2 className="h-6 w-6 animate-spin text-primary" />
    <span className="z-secondary ml-3">{label}</span>
  </div>
);

export const EmptyState = ({
  icon: Icon,
  title,
  description,
  compact = false,
}: {
  icon: typeof AlertCircle;
  title: string;
  description: string;
  compact?: boolean;
}) => (
  <div
    className={cn(
      "flex flex-col items-center justify-center gap-2 text-muted-foreground",
      compact ? "py-10" : "py-16"
    )}
  >
    <Icon className="h-10 w-10 opacity-40" />
    <p className="z-body-md text-foreground">{title}</p>
    <p className="z-secondary">{description}</p>
  </div>
);
